// RootLayout.tsx — the authenticated app shell: Sidebar + Topbar + routed
// content. Replaces the prototype's view-switching-via-store: nav is now the
// router, and the active store lives in context (sent to the API as scope).
import { Suspense, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar, Topbar } from '@/foundation/chrome';
import { useViewport } from '@/foundation/useViewport';
import { CommandPalette } from '@/foundation/CommandPalette';
import { Toasts } from '@/foundation/primitives';
import { TaskProgressFloat } from '@/foundation/TaskProgressFloat';
import { QuickActions } from '@/features/quickActions/QuickActions';
import { RequireAuth } from '@/auth/RequireAuth';
import { useLogout } from '@/auth/useLogout';
import { useMe } from '@/auth/useMe';
import { useSessionTimeout } from '@/auth/useSessionTimeout';
import { useAlerts, useSetAlertState } from '@/features/inventory/api';
import { usePrescriptions } from '@/features/prescriptions/api';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/features/stocks/notifications';
import { activeNav, navPath } from './navigation';

/** Slim activity bar under the topbar — instant loading affordance for every
 *  query/mutation without per-page spinner wiring. */
function FetchIndicator() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  if (!fetching && !mutating) return null;
  return (
    <div style={{ position: 'relative', height: 0, zIndex: 40 }}>
      <div
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, overflow: 'hidden' }}
      >
        <div
          style={{
            width: '40%',
            height: '100%',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--primary-main)',
            animation: 'pload 1s ease-in-out infinite',
          }}
        />
      </div>
    </div>
  );
}

function PageFallback() {
  return (
    <div
      style={{
        padding: 'var(--space-6)',
        color: 'var(--text-secondary)',
      }}
    >
      Loading…
    </div>
  );
}

export default function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: alerts = [] } = useAlerts();
  const { data: prescriptions = [] } = usePrescriptions();
  const { data: notifications = [] } = useNotifications();
  const markNotifRead = useMarkNotificationRead();
  const markAllNotifsRead = useMarkAllNotificationsRead();
  const setAlertState = useSetAlertState();
  const logout = useLogout();
  const { compact, phone } = useViewport();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Fetch user profile + sync backend roles → store on mount. Capabilities/nav
  // must derive from the resolved /me, never from the persisted demo role, so we
  // gate the chrome on this query and show a loader until it settles (BUG-UI-001).
  const me = useMe();
  // `isLoading` is true only while an *enabled* query is in flight — i.e. a real
  // authenticated session whose roles aren't synced yet. In mock/demo mode the
  // query is disabled, so this is false and the shell renders immediately.
  const meLoading = me.isLoading;

  // user-controlled icon rail, persisted; small laptops default to the rail
  const [rail, setRail] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('piq.ui.sidebarRail');
      if (saved !== null) return saved === '1';
    } catch {
      /* tests */
    }
    return compact;
  });
  const toggleRail = () => {
    const next = !rail;
    try {
      localStorage.setItem('piq.ui.sidebarRail', next ? '1' : '0');
    } catch {
      /* tests */
    }
    setRail(next);
  };

  // Auto-logout after 15 minutes of inactivity
  useSessionTimeout(logout);

  const active = activeNav(location.pathname);
  const onNav = (id: string) => {
    navigate(navPath(id));
    setSidebarOpen(false);
  };
  const openAlerts = alerts.filter((a) => a.status !== 'acknowledged');
  const clinicalCheckPending = prescriptions.filter(
    (p) => p.status === 'Awaiting Clinical Check' || p.status === 'Query Raised',
  ).length;

  if (meLoading) {
    // Authenticated, but roles not yet resolved — render a neutral loading shell
    // rather than nav derived from the stale/default role (BUG-UI-001).
    return (
      <RequireAuth>
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            height: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--bg-app)',
            color: 'var(--text-secondary)',
          }}
        >
          Loading your workspace…
        </div>
      </RequireAuth>
    );
  }

  return (
    <RequireAuth>
      <div
        style={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          background: 'var(--bg-app)',
        }}
      >
        {!phone && (
          <Sidebar
            active={active}
            onNav={onNav}
            alertBadge={openAlerts.length}
            rxBadge={clinicalCheckPending}
            rail={rail}
            onToggleRail={toggleRail}
          />
        )}
        {phone && sidebarOpen && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
            <div
              onClick={() => setSidebarOpen(false)}
              style={{ position: 'absolute', inset: 0, background: 'var(--overlay-backdrop)' }}
            />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                animation: 'pop .15s ease',
              }}
            >
              <Sidebar
                active={active}
                onNav={onNav}
                alertBadge={openAlerts.length}
                rxBadge={clinicalCheckPending}
              />
            </div>
          </div>
        )}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Topbar
            onNav={onNav}
            alerts={openAlerts}
            onAckAlert={(a) =>
              setAlertState.mutate({ alertKey: a.id, storeId: a.store, status: 'acknowledged' })
            }
            notifications={notifications}
            onMarkNotifRead={(id) => markNotifRead.mutate(id)}
            onOpenNotification={(path) => navigate(path)}
            rolesSyncing={meLoading}
            onMarkAllNotifsRead={() => markAllNotifsRead.mutate()}
            onMenu={phone ? () => setSidebarOpen(true) : undefined}
            onOpenSearch={() => setPaletteOpen(true)}
          />
          <FetchIndicator />
          {/* Page gutter on the spacing scale (Wave E): 12 / 20 / 32 were an
              off-grid ternary. Bottom gutter clears the docked FAB + the iOS
              safe area so the last row's actions stay tappable. */}
          <main
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: phone ? 'var(--space-3)' : compact ? 'var(--space-5)' : 'var(--space-6)',
              paddingBottom: phone
                ? 'calc(var(--space-9) + env(safe-area-inset-bottom))'
                : undefined,
            }}
          >
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
        {/* ⌘K / Ctrl+K listener lives inside the palette; the shell just holds state */}
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          onOpen={() => setPaletteOpen(true)}
        />
        <Toasts />
        <TaskProgressFloat />
        <QuickActions />
      </div>
    </RequireAuth>
  );
}
